import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronRight } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { SectionLabel, Reveal } from "@/components/platform/primitives";
import { PLATFORM_CATEGORIES, PLATFORM_PRODUCTS } from "@/data/platform";
import { setMetaDescription } from "@/lib/seo";

export default function PlatformOverview() {
  useEffect(() => {
    document.title = "Plattform · CashFUX";
    setMetaDescription(
      "Achtzehn Banking-Module auf einem gemeinsamen Ledger Core: Konten, Zahlungen, Karten, Treasury, Operations und Developer-Tools über eine API."
    );
    return () => {
      document.title = "CashFUX";
    };
  }, []);

  return (
    <div className="relative min-h-screen bg-[#05060a] text-white font-body antialiased" data-testid="platform-overview">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden pt-28 lg:pt-36 pb-16 lg:pb-20" data-testid="platform-hero">
        <div className="absolute inset-0 grid-bg mask-radial" />
        <div className="absolute -top-32 right-1/4 h-[440px] w-[440px] rounded-full bg-[#e01e37]/10 blur-[130px]" />

        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <nav className="mb-10 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/40" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Start</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-[#e01e37]">Plattform</span>
          </nav>

          <div className="max-w-4xl">
            <SectionLabel>Plattform</SectionLabel>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 font-display font-extrabold tracking-tight leading-[1.0] text-4xl sm:text-5xl lg:text-[4rem]"
            >
              Die gesamte Bank,<br />
              <span className="text-white/40">modular aufgebaut.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-7 max-w-2xl text-base lg:text-lg leading-relaxed text-white/60"
            >
              Achtzehn Module auf einem gemeinsamen Ledger Core. Kombinieren Sie
              nur, was Sie brauchen – Konten, Zahlungen, Karten, Treasury,
              Operations und Developer-Tools über eine einzige API.
            </motion.p>
          </div>

          {/* counter strip */}
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              { v: "18", l: "Module" },
              { v: "1", l: "Ledger Core" },
              { v: "API", l: "First" },
              { v: "White Label", l: "Ihre Marke" },
            ].map((s, i) => (
              <div key={i} className="bg-[#05060a] px-6 py-7">
                <div className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight">{s.v}</div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="relative pb-8" data-testid="platform-categories">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          {PLATFORM_CATEGORIES.map((cat, ci) => (
            <div key={cat.name} className="border-t border-white/10 py-14 lg:py-20" data-testid={`category-${ci}`}>
              <Reveal>
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                  <div>
                    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#e01e37]">
                      {String(ci + 1).padStart(2, "0")} — {cat.name}
                    </span>
                    <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
                      {cat.description}
                    </h2>
                  </div>
                  <span className="font-mono text-xs text-white/35">{cat.products.length} Module</span>
                </div>
              </Reveal>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                {cat.products.map((p, i) => (
                  <motion.div
                    key={p.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link
                      to={`/plattform/${p.slug}`}
                      data-testid={`module-card-${p.slug}`}
                      className="group relative flex h-full flex-col bg-[#05060a] p-7 lg:p-8 transition-colors duration-500 hover:bg-[#0a0c12]"
                    >
                      <span className="absolute left-0 top-0 h-0.5 w-0 bg-[#e01e37] transition-[width] duration-500 group-hover:w-full" />
                      <div className="flex items-start justify-between">
                        <span className="flex h-11 w-11 items-center justify-center border border-white/10 text-white/80 transition-colors duration-300 group-hover:border-[#e01e37]/50 group-hover:text-[#e01e37]">
                          <p.icon className="h-5 w-5" strokeWidth={1.5} />
                        </span>
                        <ArrowUpRight className="h-5 w-5 text-white/20 transition-all duration-300 group-hover:text-[#e01e37] group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </div>
                      <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{p.name}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-white/50">{p.tagline}</p>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-white/10 py-16 sm:py-24 lg:py-32" data-testid="platform-cta">
        <div className="absolute inset-0 grid-bg mask-radial opacity-60" />
        <div className="absolute left-1/2 top-1/2 h-[420px] w-[640px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#e01e37]/10 blur-[140px]" />
        <div className="relative mx-auto max-w-[900px] px-6 lg:px-10 text-center">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-display text-3xl sm:text-4xl lg:text-[3.25rem] font-extrabold tracking-tight leading-[1.04] text-balance">
              Stellen Sie Ihre Finanzinfrastruktur zusammen.
            </h2>
            <p className="mx-auto mt-6 max-w-lg text-base lg:text-lg text-white/55 leading-relaxed">
              Starten Sie mit einem Modul oder der gesamten Plattform. Unser Team
              begleitet Sie von der Architektur bis zum Go-Live.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="mailto:kontakt@cashfux.com"
                data-testid="platform-cta-primary"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-[#e01e37] px-8 py-4 text-sm font-medium text-white transition-[background-color] duration-300 hover:bg-[#c91830] accent-glow"
              >
                Demo anfragen
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <Link
                to="/"
                data-testid="platform-cta-secondary"
                className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 border border-white/20 px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/5"
              >
                Zur Startseite
              </Link>
            </div>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.15em] text-white/30">
              {PLATFORM_PRODUCTS.length} Module · Ein Ledger · Eine API
            </p>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
